import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { SettingsContext } from "@/App";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import {
  Pencil, Trash2, Loader2, Sparkles, ImagePlus, Film, Lightbulb, TriangleAlert, Check, X, LogIn, Monitor, Megaphone,
} from "lucide-react";
import {
  GREETING_ACCENTS, GREETING_IDEAS, LOOK_CHANGED, accentOf, formatRange, greetingStatus, mediaUrl,
} from "@/lib/greetings";

/* Admin → Announcements (3/4-Nov-2026). The admin first picks what to post:
   a regular announcement (a notice that stays up until taken down — moved
   here from General) or a holiday greeting (dates; switches on and off by
   itself, India time). Below: the login-page picture. Saves through its own
   calls, so the dialog's Save button is hidden on this tab (like Analyst). */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Upload limits mirror LOGIN_MEDIA_MAX on the server; the "recommended"
// numbers are where the amber warning starts.
const LIMIT = { image: 3 * 1024 * 1024, video: 8 * 1024 * 1024 };
const SUGGEST = { image: 1024 * 1024, video: 4 * 1024 * 1024 };
const MAX_W = 1920;
const mb = (n) => `${(n / (1024 * 1024)).toFixed(n < 1024 * 1024 ? 2 : 1)} MB`;

const BLANK = { title: "", greeting: "", message: "", start: "", end: "", show_on: "both", accent: "gold", media_id: "", enabled: true };

const STATUS_TONE = {
  live: "bg-emerald-500/12 text-emerald-600 border-emerald-500/30",
  soon: "bg-sky-500/10 text-sky-600 border-sky-500/25",
  ended: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]",
  off: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]",
};
const WHERE = { both: "Login page + app", login: "Login page only", app: "Inside the app only" };

const inputCls = "w-full h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-amber-500/60";

// Shrink a photo to 1920 px wide and re-encode as JPEG. A photo that is
// already small enough is sent untouched — re-saving a JPEG only loses detail
// (and bands dark night skies). Keeps the original if re-encoding is bigger.
async function shrinkImage(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const w = img.naturalWidth, h = img.naturalHeight;
    if (w <= MAX_W && file.size <= SUGGEST.image) return { file, width: w, height: h, origW: w, origH: h };
    const scale = Math.min(1, MAX_W / w);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale); canvas.height = Math.round(h * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.85));
    const out = blob && blob.size < file.size
      ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" })
      : file;
    return { file: out, width: canvas.width, height: canvas.height, origW: w, origH: h };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function videoShape(file) {
  return new Promise(res => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { res({ width: v.videoWidth, height: v.videoHeight, duration: v.duration }); URL.revokeObjectURL(url); };
    v.onerror = () => { res(null); URL.revokeObjectURL(url); };
    v.src = url;
  });
}

/* One upload button + the size advice under it. Calls onUploaded(meta). */
function MediaUploader({ onUploaded, testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState([]);

  const pick = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = "";
    if (!raw) return;
    const kind = raw.type.startsWith("video/") ? "video" : raw.type.startsWith("image/") ? "image" : null;
    if (!kind || !["image/jpeg", "image/png", "image/webp", "video/mp4", "video/webm"].includes(raw.type)) {
      setNotes([{ bad: true, text: "Use a JPG, PNG or WebP photo, or an MP4 or WebM video." }]);
      return;
    }
    setBusy(true);
    const warn = [];
    try {
      let file = raw, shape = null;
      if (kind === "image") {
        const r = await shrinkImage(raw);
        file = r.file;
        shape = { width: r.width, height: r.height };
        if (r.origW > MAX_W) warn.push({ text: `Shrunk from ${r.origW} px to ${r.width} px wide: ${mb(raw.size)} → ${mb(file.size)}.` });
        if (r.origW < 1280) warn.push({ text: `Only ${r.origW} px wide. It may look soft on a big screen; 1920 px is ideal.` });
      } else {
        shape = await videoShape(raw);
        if (shape && shape.duration > 20) warn.push({ text: `${Math.round(shape.duration)} s long. A 5–10 s loop is plenty and much lighter.` });
      }
      if (shape && shape.height > shape.width) warn.push({ text: "This is portrait. The login page shows a wide frame, so the top and bottom will be cut off." });
      if (file.size > LIMIT[kind]) {
        setNotes([{ bad: true, text: `${mb(file.size)} is too big. A ${kind} can be at most ${mb(LIMIT[kind])}${kind === "video" ? ". Shorten it or export at 1080p or lower." : "."}` }]);
        return;
      }
      if (file.size > SUGGEST[kind]) warn.push({ text: `${mb(file.size)} is on the heavy side. Phones on a weak signal will wait for it; under ${mb(SUGGEST[kind])} is better.` });
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/login-media`, fd);
      setNotes(warn);
      toast.success(`Uploaded (${mb(res.data.size)})`);
      onUploaded(res.data);
    } catch (err) {
      setNotes([{ bad: true, text: err.response?.data?.detail || "Upload failed." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm" className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold border border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-60"
        data-testid={testId}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
        {busy ? "Uploading…" : "Upload photo or video"}
      </button>
      <p className="mt-1.5 text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed">
        Best: a wide photo, about 1920 × 1200 px, under 1 MB (we shrink big photos for you). Or a silent MP4 loop of 5–10 s, under 4 MB.
      </p>
      {notes.map((n, i) => (
        <p key={i} className={`mt-1 flex items-start gap-1.5 text-[11.5px] ${n.bad ? "text-red-500" : "text-amber-600"}`} data-testid={`${testId}-note`}>
          <TriangleAlert className="w-3.5 h-3.5 mt-px shrink-0" />{n.text}
        </p>
      ))}
    </div>
  );
}

function MediaThumb({ m, className = "" }) {
  if (!m) {
    return <img src="/rockpecker-building-poster.jpg" alt="" className={`object-cover ${className}`} draggable={false} />;
  }
  return m.kind === "video"
    ? <video src={mediaUrl(m)} muted loop autoPlay playsInline className={`object-cover ${className}`} />
    : <img src={mediaUrl(m)} alt="" className={`object-cover ${className}`} draggable={false} />;
}

/* Small copy of the login page's photo card, so the admin sees the greeting
   the way people will. */
function LoginPreview({ greeting, message, accent, media }) {
  const a = accentOf(accent);
  return (
    <div className="relative w-full aspect-[16/10] rounded-xl overflow-hidden border border-[hsl(var(--border))] bg-slate-800" data-testid="greeting-preview">
      <MediaThumb m={media} className="absolute inset-0 w-full h-full" />
      <div className="absolute inset-x-0 bottom-0 h-[55%]" style={{ backgroundImage: "linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(15,23,42,0.45) 45%, rgba(15,23,42,0.82) 100%)" }} />
      <div className="absolute inset-x-0 bottom-0 px-4 pb-3.5">
        <p className="text-[17px] font-bold leading-tight text-white">{greeting || "Your greeting"}</p>
        {message && <p className="mt-1 text-[11.5px] leading-snug max-w-[85%]" style={{ color: a.light }}>{message}</p>}
      </div>
    </div>
  );
}

function GreetingEditor({ initial, media, today, onCancel, onSaved, onMediaAdded }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const chosen = media.find(m => m.id === f.media_id) || media.find(m => m.is_default) || null;

  const save = async () => {
    if (!f.greeting.trim()) { toast.error("Write the greeting first, e.g. Happy Diwali."); return; }
    if (!f.start || !f.end) { toast.error("Pick both dates."); return; }
    if (f.end < f.start) { toast.error("The end date is before the start date."); return; }
    setSaving(true);
    try {
      const body = { ...f, title: f.title || f.greeting };
      const res = f.id
        ? await axios.put(`${API}/admin/occasions/${f.id}`, body)
        : await axios.post(`${API}/admin/occasions`, body);
      toast.success(f.id ? "Greeting updated" : "Greeting scheduled");
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-4" data-testid="greeting-editor">
      {!f.id && (
        <div className="mb-3">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-[hsl(var(--muted-foreground))] mb-1.5">
            <Lightbulb className="w-3.5 h-3.5 text-amber-500" /> Start from an idea, then edit the words
          </p>
          <div className="flex flex-wrap gap-1.5">
            {GREETING_IDEAS.map(idea => (
              <button
                key={idea.title}
                type="button"
                onClick={() => setF(p => ({ ...p, ...idea }))}
                className={`px-2.5 py-1 rounded-full text-[11.5px] font-medium border transition-colors ${
                  f.title === idea.title ? "bg-amber-500/15 text-amber-700 border-amber-500/40" : "border-[hsl(var(--border))] hover:border-amber-500/40"
                }`}
                data-testid={`greeting-idea-${idea.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {idea.title}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-4">
        <div className="space-y-3 min-w-0">
          <label className="block">
            <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Greeting</span>
            <input value={f.greeting} maxLength={60} onChange={e => set("greeting", e.target.value)} placeholder="Happy Diwali" className={`${inputCls} mt-1`} data-testid="greeting-text" />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Message <span className="font-normal">(one short line)</span></span>
            <textarea value={f.message} maxLength={160} rows={2} onChange={e => set("message", e.target.value)} placeholder="Wishing you and your family a joyful Diwali. From all of us at Rockpecker." className={`${inputCls} h-auto py-1.5 mt-1 resize-none`} data-testid="greeting-message" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Shows from</span>
              <input type="date" value={f.start} onChange={e => set("start", e.target.value)} className={`${inputCls} mt-1`} data-testid="greeting-start" />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Until (included)</span>
              <input type="date" value={f.end} min={f.start || undefined} onChange={e => set("end", e.target.value)} className={`${inputCls} mt-1`} data-testid="greeting-end" />
            </label>
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] -mt-1">
            Comes on at midnight India time on the first day and goes off by itself after the last.
            {f.start && f.end && f.end >= f.start && <> {greetingStatus(f, today).label && <>Right now: <strong>{greetingStatus(f, today).label.toLowerCase()}</strong>.</>}</>}
          </p>

          <div>
            <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Show on</span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {[["both", "Login page + app"], ["login", "Login page"], ["app", "Inside the app"]].map(([val, label]) => (
                <button key={val} type="button" onClick={() => set("show_on", val)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    f.show_on === val ? "bg-amber-500/15 text-amber-700 border-amber-500/40" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]"
                  }`}
                  data-testid={`greeting-show-${val}`}
                >{label}</button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Colour</span>
            <div className="mt-1 flex items-center gap-2">
              {Object.entries(GREETING_ACCENTS).map(([key, a]) => (
                <button key={key} type="button" onClick={() => set("accent", key)} title={a.name} aria-label={a.name}
                  className={`w-6 h-6 rounded-full flex items-center justify-center transition-transform ${f.accent === key ? "ring-2 ring-offset-2 ring-offset-[hsl(var(--card))]" : "hover:scale-110"}`}
                  style={{ background: a.hex, "--tw-ring-color": a.hex }}
                  data-testid={`greeting-accent-${key}`}
                >
                  {f.accent === key && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Login page picture during this greeting</span>
            <div className="mt-1 flex flex-wrap gap-2">
              <button type="button" onClick={() => set("media_id", "")}
                className={`relative w-[86px] h-[54px] rounded-lg overflow-hidden border-2 ${!f.media_id ? "border-amber-500" : "border-transparent opacity-80 hover:opacity-100"}`}
                title="Same as every day" data-testid="greeting-media-everyday"
              >
                <MediaThumb m={media.find(m => m.is_default) || null} className="absolute inset-0 w-full h-full" />
                <span className="absolute inset-x-0 bottom-0 text-[9.5px] font-semibold text-white bg-black/55 py-0.5">Every day</span>
              </button>
              {media.map(m => (
                <button key={m.id} type="button" onClick={() => set("media_id", m.id)}
                  className={`relative w-[86px] h-[54px] rounded-lg overflow-hidden border-2 ${f.media_id === m.id ? "border-amber-500" : "border-transparent opacity-80 hover:opacity-100"}`}
                  title={m.filename} data-testid="greeting-media-option"
                >
                  <MediaThumb m={m} className="absolute inset-0 w-full h-full" />
                  {m.kind === "video" && <Film className="absolute top-1 right-1 w-3 h-3 text-white drop-shadow" />}
                </button>
              ))}
            </div>
            <div className="mt-2">
              <MediaUploader testId="greeting-upload" onUploaded={(m) => { onMediaAdded(m); set("media_id", m.id); }} />
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">How the login page will look</span>
          <div className="mt-1">
            <LoginPreview greeting={f.greeting} message={f.message} accent={f.accent} media={f.media_id ? chosen : media.find(m => m.is_default) || null} />
          </div>
          {f.show_on === "app" && <p className="mt-1.5 text-[11px] text-[hsl(var(--muted-foreground))]">Set to inside the app only, so the login page stays as normal.</p>}
          <span className="block mt-3 text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Top bar inside the app</span>
          <div className="mt-1 rounded-lg border border-[hsl(var(--border))] px-3 py-2 flex items-center gap-2 bg-[hsl(var(--muted))]/50">
            <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: accentOf(f.accent).hex }}>
              <Sparkles className="w-3 h-3 text-white" strokeWidth={2.4} />
            </span>
            <span className="text-[11.5px] leading-snug min-w-0">
              <strong>{f.greeting || "Your greeting"}</strong>{f.message && <span className="text-[hsl(var(--muted-foreground))]"> · {f.message}</span>}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="h-8 px-3 rounded-lg text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]" data-testid="greeting-cancel">Cancel</button>
        <button type="button" onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 h-8 px-4 rounded-lg text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 transition-colors disabled:opacity-60"
          data-testid="greeting-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          {f.id ? "Save changes" : "Schedule greeting"}
        </button>
      </div>
    </div>
  );
}

/* The regular announcement (settings.announcement). Saved on its own, as a
   partial settings update, so it never rides along with the General tab's
   Save (which now leaves `announcement` out). */
function AnnouncementSection({ open, onOpen, onClose }) {
  const { settings, fetchSettings } = useContext(SettingsContext);
  const ann = settings?.announcement || {};
  const [text, setText] = useState("");
  const [showOn, setShowOn] = useState("both");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setText(ann.text || ""); setShowOn(ann.show_on || "both"); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (next, done) => {
    setBusy(true);
    try {
      await axios.put(`${API}/settings`, { announcement: next });
      await fetchSettings();
      toast.success(done);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the announcement");
    } finally {
      setBusy(false);
    }
  };
  const takeDown = () => {
    if (!window.confirm("Take the announcement down? It disappears for everyone straight away.")) return;
    save({ text: "", show_on: ann.show_on || "both" }, "Announcement taken down");
  };

  if (open) {
    return (
      <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-4" data-testid="announcement-editor">
        <label className="block">
          <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Message</span>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="e.g. System maintenance Sunday 2–4 AM IST · No production changes during this window"
            rows={2}
            maxLength={200}
            autoFocus
            className={`${inputCls} h-auto py-1.5 mt-1 resize-none`}
            data-testid="settings-announcement-text"
          />
        </label>
        <p className="mt-0.5 text-right text-[10.5px] text-[hsl(var(--muted-foreground))] tabular-nums">{text.length}/200</p>
        <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Show on</span>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {[["both", "Login page + app"], ["login", "Login page"], ["app", "Inside the app"]].map(([val, label]) => (
            <button key={val} type="button" onClick={() => setShowOn(val)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                showOn === val ? "bg-amber-500/15 text-amber-700 border-amber-500/40" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]"
              }`}
              data-testid={`settings-announcement-show-${val}`}
            >{label}</button>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">
          Stays up until you take it down. Anyone who closed an earlier message sees it again when the words change.
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="h-8 px-3 rounded-lg text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]" data-testid="announcement-cancel">Cancel</button>
          <button type="button" disabled={busy || !text.trim()}
            onClick={() => save({ text: text.trim(), show_on: showOn }, ann.text ? "Announcement updated" : "Announcement is up")}
            className="inline-flex items-center gap-1.5 h-8 px-4 rounded-lg text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 transition-colors disabled:opacity-50"
            data-testid="announcement-save"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {ann.text ? "Save changes" : "Put it up"}
          </button>
        </div>
      </div>
    );
  }

  if (!ann.text) {
    return (
      <div className="mt-3 rounded-xl border border-dashed border-[hsl(var(--border))] px-4 py-4 text-center text-xs text-[hsl(var(--muted-foreground))]" data-testid="announcement-empty">
        No announcement up right now.
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] px-3 py-2.5" data-testid="announcement-row">
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-amber-500">
        <Megaphone className="w-4 h-4 text-white" strokeWidth={2.3} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[hsl(var(--foreground))] break-words">{ann.text}</p>
        <p className="text-[11.5px] text-[hsl(var(--muted-foreground))] mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span className="px-2 py-px rounded-full text-[10.5px] font-semibold border bg-emerald-500/12 text-emerald-600 border-emerald-500/30">Live · until you take it down</span>
          <span className="inline-flex items-center gap-1">
            {ann.show_on !== "app" && <LogIn className="w-3 h-3" />}{ann.show_on !== "login" && <Monitor className="w-3 h-3" />}
            {WHERE[ann.show_on] || WHERE.both}
          </span>
        </p>
      </div>
      <button type="button" onClick={onOpen} className="p-1.5 rounded-md hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]" title="Edit" data-testid="announcement-edit">
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={takeDown} disabled={busy} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11.5px] font-semibold text-red-500 hover:bg-red-500/10" data-testid="settings-announcement-clear">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Take down
      </button>
    </div>
  );
}

/* The first choice: what kind of message to post. */
function ChoiceCard({ icon: Icon, iconBg, title, text, onClick, testId }) {
  return (
    <button type="button" onClick={onClick}
      className="group flex items-start gap-3 text-left rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3.5 py-3 hover:border-amber-500/50 hover:bg-amber-500/[0.04] transition-colors"
      data-testid={testId}
    >
      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: iconBg }}>
        <Icon className="w-[18px] h-[18px] text-white" strokeWidth={2.3} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-[hsl(var(--foreground))]">{title}</span>
        <span className="block text-[11.5px] leading-snug text-[hsl(var(--muted-foreground))] mt-0.5">{text}</span>
      </span>
    </button>
  );
}

export default function AnnouncementsPanel() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // greeting being edited, or BLANK for a new one
  const [busyId, setBusyId] = useState(null);
  const [annOpen, setAnnOpen] = useState(false);
  const annRef = useRef(null);
  const greetRef = useRef(null);
  const choose = (kind) => {
    if (kind === "announcement") { setEditing(null); setAnnOpen(true); }
    else { setAnnOpen(false); setEditing({ ...BLANK }); }
    // Bring the opened editor into view below the two cards.
    setTimeout(() => (kind === "announcement" ? annRef : greetRef).current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/login-look`);
      setData(res.data);
      window.dispatchEvent(new Event(LOOK_CHANGED));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load greetings");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--muted-foreground))]" /></div>;
  }

  const { today, occasions, media } = data;
  const everyday = media.find(m => m.is_default) || null;
  // Live and upcoming first (soonest at the top), then the ended ones.
  const ordered = [...occasions].sort((a, b) => {
    const ea = a.end < today, eb = b.end < today;
    if (ea !== eb) return ea ? 1 : -1;
    return ea ? b.start.localeCompare(a.start) : a.start.localeCompare(b.start);
  });

  const remove = async (o) => {
    if (!window.confirm(`Delete "${o.greeting}"? It will stop showing straight away.`)) return;
    setBusyId(o.id);
    try { await axios.delete(`${API}/admin/occasions/${o.id}`); toast.success("Greeting deleted"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
    finally { setBusyId(null); }
  };
  const setEveryday = async (id) => {
    try { await axios.put(`${API}/admin/login-media/default`, { media_id: id }); toast.success(id ? "Login picture changed" : "Back to the building video"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change the picture"); }
  };
  const deleteMedia = async (m) => {
    const users = occasions.filter(o => o.media_id === m.id).length;
    if (!window.confirm(`Delete this ${m.kind}?${m.is_default ? " The login page goes back to the building video." : ""}${users ? ` ${users} greeting${users > 1 ? "s" : ""} using it will fall back to the everyday picture.` : ""}`)) return;
    try { await axios.delete(`${API}/admin/login-media/${m.id}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="pb-4" data-testid="announcements-panel">
      {/* ── First choice: what to post ── */}
      <p className="mt-4 text-sm font-semibold text-[hsl(var(--foreground))]">What would you like to post?</p>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ChoiceCard icon={Megaphone} iconBg="#f59e0b" title="Announcement" testId="choose-announcement"
          text="A notice for everyone, like a maintenance window or a closure. Stays up until you take it down."
          onClick={() => choose("announcement")} />
        <ChoiceCard icon={Sparkles} iconBg="linear-gradient(135deg, #f59e0b, #e11d48)" title="Holiday greeting" testId="greeting-new"
          text="A festive wish for the team. Set the dates once: it comes on and goes off by itself."
          onClick={() => choose("greeting")} />
      </div>

      {/* ── Section 1: Announcement ── */}
      <div ref={annRef} className="mt-7 mb-1.5 scroll-mt-2">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Announcement</p>
      </div>
      <AnnouncementSection open={annOpen} onOpen={() => choose("announcement")} onClose={() => setAnnOpen(false)} />

      {/* ── Section 2: Holiday greetings ── */}
      <div ref={greetRef} className="mt-7 mb-1.5 scroll-mt-2">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Holiday greetings</p>
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        Set the dates once. Each greeting comes on and goes off by itself (India time), on the login page and in the top bar.
      </p>

      {editing && !editing.id && (
        <GreetingEditor initial={editing} media={media} today={today} onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onMediaAdded={(m) => setData(d => ({ ...d, media: [m, ...d.media] }))} />
      )}

      <div className="mt-3 space-y-2">
        {ordered.length === 0 && !editing && (
          <div className="rounded-xl border border-dashed border-[hsl(var(--border))] px-4 py-6 text-center text-xs text-[hsl(var(--muted-foreground))]" data-testid="greetings-empty">
            Nothing scheduled. Choose Holiday greeting above to add one for the next festival.
          </div>
        )}
        {ordered.map(o => {
          if (editing && editing.id === o.id) {
            return (
              <GreetingEditor key={o.id} initial={{ ...BLANK, ...o }} media={media} today={today} onCancel={() => setEditing(null)}
                onSaved={() => { setEditing(null); load(); }}
                onMediaAdded={(m) => setData(d => ({ ...d, media: [m, ...d.media] }))} />
            );
          }
          const st = greetingStatus(o, today);
          const a = accentOf(o.accent);
          const pic = o.media_id ? media.find(m => m.id === o.media_id) : null;
          return (
            <div key={o.id} className={`flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] px-3 py-2.5 ${st.key === "ended" ? "opacity-60" : ""}`} data-testid="greeting-row">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: a.hex }}>
                <Sparkles className="w-4 h-4 text-white" strokeWidth={2.3} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">{o.greeting}</p>
                  <span className={`px-2 py-px rounded-full text-[10.5px] font-semibold border ${STATUS_TONE[st.key] || STATUS_TONE.ended}`} data-testid="greeting-status">{st.label}</span>
                </div>
                <p className="text-[11.5px] text-[hsl(var(--muted-foreground))] mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span>{formatRange(o.start, o.end)}</span>
                  <span aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-1">
                    {o.show_on !== "app" && <LogIn className="w-3 h-3" />}{o.show_on !== "login" && <Monitor className="w-3 h-3" />}
                    {WHERE[o.show_on] || WHERE.both}
                  </span>
                  {pic && <><span aria-hidden="true">·</span><span>own {pic.kind}</span></>}
                </p>
              </div>
              {pic && <MediaThumb m={pic} className="w-14 h-9 rounded-md shrink-0" />}
              <button type="button" onClick={() => { setAnnOpen(false); setEditing(o); }} className="p-1.5 rounded-md hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]" title="Edit" data-testid="greeting-edit">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => remove(o)} disabled={busyId === o.id} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-500" title="Delete" data-testid="greeting-delete">
                {busyId === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Section: Login page picture ── */}
      <div className="mt-7 mb-1.5">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Login page picture</p>
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        What the login page shows on normal days. A greeting with its own picture replaces it for those dates only.
      </p>
      <div className="mt-3 flex flex-wrap gap-3">
        <div className={`w-[168px] rounded-xl border-2 overflow-hidden ${!everyday ? "border-amber-500" : "border-[hsl(var(--border))]"}`} data-testid="login-media-builtin">
          <img src="/rockpecker-building-poster.jpg" alt="" className="w-full h-[100px] object-cover" draggable={false} />
          <div className="px-2.5 py-2">
            <p className="text-[11.5px] font-semibold flex items-center gap-1"><Film className="w-3 h-3" /> Building video</p>
            {!everyday
              ? <p className="text-[10.5px] text-amber-600 font-semibold mt-0.5 flex items-center gap-1"><Check className="w-3 h-3" /> In use</p>
              : <button type="button" onClick={() => setEveryday("")} className="text-[10.5px] font-semibold text-amber-700 hover:underline mt-0.5" data-testid="login-media-use-builtin">Use this</button>}
          </div>
        </div>
        {media.map(m => (
          <div key={m.id} className={`w-[168px] rounded-xl border-2 overflow-hidden ${m.is_default ? "border-amber-500" : "border-[hsl(var(--border))]"}`} data-testid="login-media-card">
            <div className="relative">
              <MediaThumb m={m} className="w-full h-[100px]" />
              <button type="button" onClick={() => deleteMedia(m)} className="absolute top-1.5 right-1.5 p-1 rounded-md bg-black/55 text-white hover:bg-red-600" title="Delete" data-testid="login-media-delete">
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="px-2.5 py-2">
              <p className="text-[11.5px] font-semibold truncate" title={m.filename}>{m.filename || (m.kind === "video" ? "Video" : "Photo")}</p>
              <p className="text-[10.5px] text-[hsl(var(--muted-foreground))]">{m.kind === "video" ? "Video" : "Photo"} · {mb(m.size)}</p>
              {m.is_default
                ? <p className="text-[10.5px] text-amber-600 font-semibold mt-0.5 flex items-center gap-1"><Check className="w-3 h-3" /> In use</p>
                : <button type="button" onClick={() => setEveryday(m.id)} className="text-[10.5px] font-semibold text-amber-700 hover:underline mt-0.5" data-testid="login-media-use">Use every day</button>}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3">
        <MediaUploader testId="login-media-upload" onUploaded={(m) => setData(d => ({ ...d, media: [m, ...d.media] }))} />
      </div>
    </div>
  );
}
